import { useRef, useEffect, useState, useCallback } from 'react';
import { TextLayer } from 'pdfjs-dist';
import type { PDFDocumentProxy } from '../../lib/pdfWorker';
import '../../lib/pdfWorker';
import type { FieldDescriptor, ViewerMode } from './signingTypes';
import { usePdfPage } from './usePdfPage';
import FieldOverlay from './FieldOverlay';
import { PDF_VIEWER } from './pdfViewerTokens';
import { APP } from '../app/tokens';
import CommentPins from '../app/CommentPins';
import InlineCommentForm from '../app/InlineCommentForm';
import type { CommentPinData } from '@corner/shared';

const PDF_TEXT_LAYER_CSS = `
.cornerPdfTextLayer {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  line-height: 1;
  z-index: 2;
  text-align: initial;
  transform-origin: 0 0;
  user-select: text;
  -webkit-user-select: text;
}
.cornerPdfTextLayer span,
.cornerPdfTextLayer br {
  color: transparent;
  position: absolute;
  white-space: pre;
  cursor: text;
  transform-origin: 0% 0%;
}
`;

interface PdfPageCanvasProps {
  document: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
  fields: FieldDescriptor[];
  activeFieldId: string | null;
  inspectedFieldId?: string | null;
  mode: ViewerMode;
  signaturePreview?: string | null;
  /** Stable doc identifier — used by the placeholder values store key. */
  documentId?: string;
  onFieldClick?: (fieldId: string) => void;
  onFieldInspect?: (field: FieldDescriptor) => void;
  onFieldMove?: (fieldId: string, rect: { x: number; y: number; width: number; height: number }) => void;
  onVisible?: (pageNumber: number) => void;
  /** v1 placement-redesign: forwarded to FieldOverlay → FieldMarker. */
  onRequestDelete?: (fieldId: string) => void;
  onConfirmField?: (fieldId: string) => void;
  useSignerPalette?: boolean;
  /** v1 placement-redesign: when set, the page accepts a single click
   *  anywhere on its canvas and reports the click as percent coords
   *  (relative to the page). Used to place a new marker. */
  onPagePlacementClick?: (pageNumber: number, xPct: number, yPct: number) => void;
  /** When true, the page shows a crosshair cursor + hint that placement
   *  is armed. */
  placementArmed?: boolean;
  /** Render pdf.js text layer so the user can select text (e.g. Ask Corner). */
  enableTextSelection?: boolean;
  /** When user selects text and clicks the floating Ask Corner control. */
  onAskCornerAboutSelection?: (snippet: { text: string; page: number }) => void;
  commentPins?: CommentPinData[];
  activeCommentPinId?: string | null;
  onCommentPinClick?: (pinId: string) => void;
  onCommentPinMove?: (pinId: string, xPercent: number, yPercent: number) => void;
  commentMode?: boolean;
  pendingComment?: { xPercent: number; yPercent: number; pageNumber: number; quote?: string } | null;
  onDocumentCommentClick?: (xPercent: number, yPercent: number, pageNumber: number) => void;
  onCommentSubmit?: (body: string) => void;
  onCommentCancel?: () => void;
}

export default function PdfPageCanvas({
  document, pageNumber, scale, fields, activeFieldId, inspectedFieldId, mode,
  signaturePreview, documentId, onFieldClick, onFieldInspect, onFieldMove, onVisible,
  onRequestDelete, onConfirmField, useSignerPalette,
  onPagePlacementClick, placementArmed, enableTextSelection, onAskCornerAboutSelection,
  commentPins, activeCommentPinId, onCommentPinClick, onCommentPinMove, commentMode,
  pendingComment, onDocumentCommentClick, onCommentSubmit, onCommentCancel,
}: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const textLayerTaskRef = useRef<TextLayer | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [selectionToolbar, setSelectionToolbar] = useState<{ left: number; top: number; quote: string } | null>(null);

  const showAskCorner =
    Boolean(enableTextSelection && onAskCornerAboutSelection && !placementArmed);

  // Lazy rendering via IntersectionObserver
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        const vis = entry.isIntersecting;
        setIsVisible(vis);
        if (vis) onVisible?.(pageNumber);
      },
      { rootMargin: '200px 0px' },  // pre-render 200px before visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [pageNumber, onVisible]);

  const { width, height } = usePdfPage(canvasRef, document, pageNumber, scale, isVisible);

  useEffect(() => {
    if (!enableTextSelection || !document || !isVisible || !textLayerRef.current) return;

    const layerEl = textLayerRef.current;
    let cancelled = false;

    (async () => {
      try {
        const page = await document.getPage(pageNumber);
        if (cancelled || !layerEl) return;
        const viewport = page.getViewport({ scale });
        const textContent = await page.getTextContent();
        if (cancelled || !layerEl) return;

        textLayerTaskRef.current?.cancel();
        textLayerTaskRef.current = null;
        layerEl.replaceChildren();

        const textLayer = new TextLayer({
          textContentSource: textContent,
          container: layerEl,
          viewport,
        });
        textLayerTaskRef.current = textLayer;
        await textLayer.render();
        if (cancelled) {
          textLayer.cancel();
        }
      } catch (e) {
        const msg = (e as Error)?.message ?? '';
        if (!cancelled && !msg.includes('Rendering cancelled') && !msg.includes('cancelled')) {
          console.warn('[PdfPageCanvas] text layer error:', e);
        }
      }
    })();

    return () => {
      cancelled = true;
      textLayerTaskRef.current?.cancel();
      textLayerTaskRef.current = null;
      layerEl.replaceChildren();
    };
  }, [document, pageNumber, scale, isVisible, enableTextSelection]);

  const handleMouseUp = useCallback(() => {
    if (!showAskCorner || !onAskCornerAboutSelection || !textLayerRef.current || !containerRef.current) {
      setSelectionToolbar(null);
      return;
    }
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) {
      setSelectionToolbar(null);
      return;
    }
    const quote = sel.toString().replace(/\s+/g, ' ').trim();
    if (!quote) {
      setSelectionToolbar(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!textLayerRef.current.contains(range.commonAncestorContainer)) {
      setSelectionToolbar(null);
      return;
    }
    const rect = range.getBoundingClientRect();
    const cr = containerRef.current.getBoundingClientRect();
    setSelectionToolbar({
      left: rect.left + rect.width / 2 - cr.left,
      top: rect.top - cr.top - 8,
      quote,
    });
  }, [showAskCorner, onAskCornerAboutSelection]);

  useEffect(() => {
    if (!selectionToolbar) return;
    const toolbarId = `pdf-selection-toolbar-${pageNumber}`;
    const handleClick = (e: MouseEvent) => {
      const toolbar = globalThis.document.getElementById(toolbarId);
      if (toolbar?.contains(e.target as Node)) return;
      setSelectionToolbar(null);
    };
    globalThis.document.addEventListener('mousedown', handleClick);
    return () => globalThis.document.removeEventListener('mousedown', handleClick);
  }, [selectionToolbar, pageNumber]);

  // Estimate height before render for placeholder
  const estimatedHeight = width > 0 ? height : (scale * 792); // fallback to US Letter height

  const handlePlacementClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placementArmed || !onPagePlacementClick) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xPct = ((e.clientX - rect.left) / rect.width) * 100;
    const yPct = ((e.clientY - rect.top) / rect.height) * 100;
    onPagePlacementClick(pageNumber, xPct, yPct);
  };

  const handleCommentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!commentMode || !onDocumentCommentClick) return;
    if ((e.target as HTMLElement).closest('[data-comment-interactive="true"]')) return;
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const xPercent = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const yPercent = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    onDocumentCommentClick(xPercent, yPercent, pageNumber);
  };

  return (
    <div
      ref={containerRef}
      data-page={pageNumber}
      onClick={placementArmed ? handlePlacementClick : commentMode ? handleCommentClick : undefined}
      onMouseUp={showAskCorner ? handleMouseUp : undefined}
      style={{
        position: 'relative',
        width: width || '100%',
        height: height || estimatedHeight,
        backgroundColor: PDF_VIEWER.bgSheet,
        boxShadow: PDF_VIEWER.sheetShadow,
        borderRadius: 2,
        overflow: 'hidden',
        flexShrink: 0,
        cursor: placementArmed || commentMode ? 'crosshair' : undefined,
      }}
    >
      <style>{PDF_TEXT_LAYER_CSS}</style>
      <canvas
        ref={canvasRef}
        style={{ position: 'absolute', inset: 0, zIndex: 0 }}
      />
      {enableTextSelection ? (
        <div
          ref={textLayerRef}
          className="cornerPdfTextLayer"
          style={{ pointerEvents: placementArmed ? 'none' : 'auto' }}
          aria-hidden
        />
      ) : null}
      {selectionToolbar && showAskCorner && onAskCornerAboutSelection ? (
        <div
          id={`pdf-selection-toolbar-${pageNumber}`}
          style={{
            position: 'absolute',
            left: selectionToolbar.left,
            top: Math.max(4, selectionToolbar.top),
            transform: 'translate(-50%, -100%)',
            zIndex: 20,
            display: 'flex',
            gap: 4,
          }}
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onAskCornerAboutSelection({ text: selectionToolbar.quote, page: pageNumber });
              setSelectionToolbar(null);
              window.getSelection()?.removeAllRanges();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '6px 12px',
              borderRadius: 8,
              border: 'none',
              backgroundColor: '#D97645',
              color: '#FDFCFB',
              fontFamily: APP.fontFamily,
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              whiteSpace: 'nowrap',
            }}
          >
            Ask Corner
          </button>
        </div>
      ) : null}
      <FieldOverlay
        fields={fields}
        activeFieldId={activeFieldId}
        inspectedFieldId={inspectedFieldId}
        mode={mode}
        signaturePreview={signaturePreview}
        documentId={documentId}
        onFieldClick={onFieldClick}
        onFieldInspect={onFieldInspect}
        onFieldMove={onFieldMove}
        onRequestDelete={onRequestDelete}
        onConfirmField={onConfirmField}
        useSignerPalette={useSignerPalette}
      />
      {commentPins && commentPins.length > 0 ? (
        <CommentPins
          pins={commentPins}
          activePinId={activeCommentPinId}
          onPinClick={onCommentPinClick}
          onPinMove={onCommentPinMove}
          currentPage={pageNumber}
        />
      ) : null}
      {pendingComment?.pageNumber === pageNumber && onCommentSubmit && onCommentCancel ? (
        <div
          data-comment-interactive="true"
          style={{
            position: 'absolute',
            left: `${Math.min(pendingComment.xPercent * 100, 60)}%`,
            top: `${pendingComment.yPercent * 100}%`,
            zIndex: 55,
            transform: 'translate(0, 4px)',
          }}
        >
          <InlineCommentForm
            quote={pendingComment.quote}
            onSubmit={onCommentSubmit}
            onCancel={onCommentCancel}
          />
        </div>
      ) : null}
    </div>
  );
}
